import { ButtonFlavor, ButtonType, ExButton, ExLoader } from '@boomi/exosphere';
import { useEffect, useRef, useState } from 'react';
import { SYSTEM_VALUE_IDS } from '../../../constants/system';
import { getValue, saveValue } from '../../../sources/value';
import translations from '../../../translations';
import type { ValueElementResponseAPI } from '../../../types/value';
import { stringReplace } from '../../../utils/string';
import Dependents from '../../Dependents';
import CopyableText from '../../generic/CopyableText';
import Footer from '../../generic/Footer';
import ConfirmModal from '../../generic/modal/ConfirmModal';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import ValueEditor, { isDuplicateValueByName, isValid } from '../../values/editor/ValueEditor';
import { useValues } from './ValuesProvider';

const ValueDetail = () => {
    const { valueToEdit, returnToValueList } = useValues();
    const [value, setValue] = useState<ValueElementResponseAPI>(valueToEdit);
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [duplicateWarning, setDuplicateWarning] = useState<boolean>(false);

    const modalContainerRef = useRef(null);

    const dispatchNotification = useNotificationDispatch();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to add all dependencies
    useEffect(() => {
        const load = async () => {
            if (!valueToEdit.id) {
                return;
            }

            setSaving(true);

            try {
                setValue(await getValue(valueToEdit.id));
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setSaving(false);
            }
        };

        load();
    }, [dispatchNotification]);

    const onSubmit = async () => {
        setDuplicateWarning(false);

        if (SYSTEM_VALUE_IDS.includes(value.id)) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: 'Saving system values is not allowed.',
                },
            });
        } else {
            setHasSubmitted(true);

            if (isValid(value)) {
                setSaving(true);

                try {
                    if (await isDuplicateValueByName(value)) {
                        setDuplicateWarning(true);
                        return;
                    }
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error,
                        },
                    });
                } finally {
                    setSaving(false);
                }

                await onSave();
            }
        }
    };

    const onSave = async () => {
        setSaving(true);

        try {
            await saveValue(value);
            returnToValueList();
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setSaving(false);
        }
    };

    const trimmedName = value.developerName.trim();

    return (
        <>
            <div className="admin-page value-detail" ref={modalContainerRef}>
                <h1>Value: {value.developerName || ''}</h1>
                {value.id ? (
                    <CopyableText
                        wrapperClassName="value-id"
                        copyableText={value.id}
                        labelText="ID: "
                        hasCopyButton={true}
                    />
                ) : null}
                <ConfirmModal
                    show={duplicateWarning}
                    title={`Are you sure you want to name your Value: ${trimmedName}?`}
                    messages={[
                        stringReplace(translations.VS_duplicate_value_1, trimmedName),
                        translations.VS_duplicate_value_2,
                    ]}
                    onCancel={() => setDuplicateWarning(false)}
                    onConfirm={onSave}
                    container={modalContainerRef.current}
                />
                <ValueEditor value={value} onChange={setValue} showValidation={hasSubmitted} />
                {valueToEdit.id ? <Dependents id={valueToEdit.id} /> : null}
                {saving ? <ExLoader /> : null}
            </div>
            <Footer>
                <ExButton
                    title="Back"
                    onClick={returnToValueList}
                    flavor={ButtonFlavor.BRANDED}
                    type={ButtonType.SECONDARY}
                >
                    Back
                </ExButton>
                <ExButton
                    title="Save Value"
                    onClick={onSubmit}
                    flavor={ButtonFlavor.BRANDED}
                    type={ButtonType.PRIMARY}
                >
                    Save Value
                </ExButton>
            </Footer>
        </>
    );
};

export default ValueDetail;
